import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ItemPresupuesto, Rubro } from '../types';

const COLLECTION_NAME = 'items_presupuesto';

// Access subcollection: obras/{obraId}/items_presupuesto
const getCollectionRef = (obraId: string) => collection(db, 'obras', obraId, COLLECTION_NAME);

// Helper to remove undefined fields
const cleanData = <T extends Record<string, any>>(data: T): T => {
    const cleaned = { ...data };
    Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === undefined) {
            delete cleaned[key];
        }
    });
    return cleaned;
};

export const presupuestoService = {
    // Get all budget items for a specific Obra
    getItems: async (obraId: string): Promise<ItemPresupuesto[]> => {
        try {
            if (!obraId) return [];
            const q = query(getCollectionRef(obraId));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as ItemPresupuesto));
        } catch (error) {
            console.error("Error getting budget items:", error);
            throw error;
        }
    },

    // Add a new budget item
    addItem: async (obraId: string, item: Omit<ItemPresupuesto, 'id'>): Promise<string> => {
        try {
            const cleanedItem = cleanData(item);
            console.log("Adding item with cleaned data:", cleanedItem); // Debug log
            const docRef = await addDoc(getCollectionRef(obraId), cleanedItem);
            return docRef.id;
        } catch (error) {
            console.error("Error adding budget item:", error);
            throw error;
        }
    },

    // Update an existing budget item
    updateItem: async (obraId: string, itemId: string, updates: Partial<ItemPresupuesto>): Promise<void> => {
        try {
            const cleanedUpdates = cleanData(updates);
            const docRef = doc(db, 'obras', obraId, COLLECTION_NAME, itemId);
            await updateDoc(docRef, cleanedUpdates);
        } catch (error) {
            console.error("Error updating budget item:", error);
            throw error;
        }
    },

    // Delete a budget item
    deleteItem: async (obraId: string, itemId: string): Promise<void> => {
        try {
            const docRef = doc(db, 'obras', obraId, COLLECTION_NAME, itemId);
            await deleteDoc(docRef);
        } catch (error) {
            console.error("Error deleting budget item:", error);
            throw error;
        }
    },

    // --- RUBROS METHODS ---

    // Get all rubros for a specific Obra
    getRubros: async (obraId: string): Promise<Rubro[]> => {
        try {
            if (!obraId) return [];
            const q = query(collection(db, 'obras', obraId, 'rubros'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Rubro)).sort((a, b) => (a.order || 0) - (b.order || 0));
        } catch (error) {
            console.error("Error getting rubros:", error);
            throw error;
        }
    },

    // Add a new rubro
    addRubro: async (obraId: string, rubro: Omit<Rubro, 'id'>): Promise<string> => {
        try {
            const cleanedRubro = cleanData(rubro);
            const docRef = await addDoc(collection(db, 'obras', obraId, 'rubros'), cleanedRubro);
            return docRef.id;
        } catch (error) {
            console.error("Error adding rubro:", error);
            throw error;
        }
    },

    // Delete a rubro
    deleteRubro: async (obraId: string, rubroId: string): Promise<void> => {
        try {
            // Optional: Check if items exist in this rubro before deleting? 
            // For now, we'll just delete the rubro. Logic to move items to "Sin Rubro" should be in UI/Controller
            const docRef = doc(db, 'obras', obraId, 'rubros', rubroId);
            await deleteDoc(docRef);
        } catch (error) {
            console.error("Error deleting rubro:", error);
            throw error;
        }
    },

    // Update an existing rubro
    updateRubro: async (obraId: string, rubroId: string, updates: Partial<Rubro>): Promise<void> => {
        try {
            const cleanedUpdates = cleanData(updates);
            const docRef = doc(db, 'obras', obraId, 'rubros', rubroId);
            await updateDoc(docRef, cleanedUpdates);
        } catch (error) {
            console.error("Error updating rubro:", error);
            throw error;
        }
    },

    // Batch add budget (Rubros and Items)
    batchAddBudget: async (obraId: string, rubros: Rubro[], items: ItemPresupuesto[]): Promise<void> => {
        try {
            const batch = writeBatch(db);

            // Add Rubros
            rubros.forEach(rubro => {
                const docRef = doc(db, 'obras', obraId, 'rubros', rubro.id);
                batch.set(docRef, cleanData(rubro));
            });

            // Add Items
            items.forEach(item => {
                const docRef = doc(db, 'obras', obraId, COLLECTION_NAME, item.id);
                batch.set(docRef, cleanData(item));
            });

            await batch.commit();
        } catch (error) {
            console.error("Error batch adding budget:", error);
            throw error;
        }
    },

    // Bulk delete items and rubros
    bulkDelete: async (obraId: string, itemIds: string[], rubroIds: string[]): Promise<void> => {
        try {
            const batch = writeBatch(db);

            // Delete Items
            itemIds.forEach(id => {
                const docRef = doc(db, 'obras', obraId, COLLECTION_NAME, id);
                batch.delete(docRef);
            });

            // Delete Rubros
            rubroIds.forEach(id => {
                const docRef = doc(db, 'obras', obraId, 'rubros', id);
                batch.delete(docRef);
            });

            await batch.commit();
        } catch (error) {
            console.error("Error in bulk delete service:", error);
            throw error;
        }
    }
};
